// SuperProductGroupsPage — /super/product-groups.
//
// Migration 169. Platform admins manage GLOBAL product groups (company_id
// NULL) shared across every tenant. Tenants see these read-only and opt out
// via their own exclusion list. Delete is a soft-delete (deleted_at) — a hard
// delete would SET NULL group_id on every tenant's assigned products.

import { useState } from 'react';
import { Layers, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import {
  useCreateGlobalGroup,
  useDeleteGlobalGroup,
  useSuperProductGroups,
  useUpdateGlobalGroup,
  type SuperProductGroup,
} from './hooks/useSuper';
import { extractErrorMessage } from '../../utils/errorMessage';
import styles from './SuperProductGroupsPage.module.css';

export function SuperProductGroupsPage(): JSX.Element {
  const groupsQuery = useSuperProductGroups();
  const groups = groupsQuery.data?.groups ?? [];

  const createMut = useCreateGlobalGroup();
  const deleteMut = useDeleteGlobalGroup();

  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SuperProductGroup | null>(null);

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newName.trim() === '' || newCode.trim() === '') {
      setError('Name and code are required.');
      return;
    }
    createMut.mutate(
      { name: newName.trim(), code: newCode.trim() },
      {
        onSuccess: () => {
          setNewName('');
          setNewCode('');
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  }

  function handleDeleteConfirm() {
    if (deleteTarget === null) return;
    deleteMut.mutate(deleteTarget.enc_id, {
      onSuccess: () => setDeleteTarget(null),
      onError: (err) => {
        setDeleteTarget(null);
        setError(extractErrorMessage(err));
      },
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Global Product Groups"
        subtitle="Platform-wide weighbridge product groups. Every tenant sees these unless they opt out."
      />

      {error ? <p className={styles.error}>{error}</p> : null}

      <Card title="Create global group">
        <form className={styles.createRow} onSubmit={handleCreate}>
          <input
            type="text"
            placeholder="Group name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            aria-label="New global group name"
          />
          <input
            type="text"
            placeholder="CODE"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            onBlur={(e) => setNewCode(e.target.value.toUpperCase())}
            aria-label="New global group code"
          />
          <Button type="submit" variant="primary" icon={Plus} loading={createMut.isPending}>
            Create
          </Button>
        </form>
      </Card>

      <Card title="Global groups" flush>
        {groupsQuery.isLoading ? (
          <div className={styles.loading}>
            <Spinner label="Loading groups" />
          </div>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No global groups yet"
            description="Create a global group above to share it with every tenant."
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Sort</th>
                <th>Status</th>
                <th>Products</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <SuperGroupRow
                  key={g.code}
                  group={g}
                  onDelete={() => setDeleteTarget(g)}
                  onError={setError}
                />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete global group?"
        message={
          <p>
            Delete <strong>{deleteTarget?.name ?? 'this group'}</strong>?
            {deleteTarget && deleteTarget.product_count ? (
              <>
                {' '}
                {deleteTarget.product_count} product
                {deleteTarget.product_count === 1 ? '' : 's'} across tenants will
                become ungrouped.
              </>
            ) : null}{' '}
            This soft-deletes the group.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMut.isPending}
      />
    </div>
  );
}

interface SuperGroupRowProps {
  group: SuperProductGroup;
  onDelete: () => void;
  onError: (msg: string) => void;
}

function SuperGroupRow({ group, onDelete, onError }: SuperGroupRowProps): JSX.Element {
  const updateMut = useUpdateGlobalGroup();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [code, setCode] = useState(group.code);
  const [sortOrder, setSortOrder] = useState(String(group.sort_order));

  function save() {
    updateMut.mutate(
      {
        encId: group.enc_id,
        body: {
          name: name.trim(),
          code: code.trim(),
          sort_order: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        },
      },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => onError(extractErrorMessage(err)),
      },
    );
  }

  function toggleActive() {
    updateMut.mutate(
      { encId: group.enc_id, body: { is_active: !group.is_active } },
      { onError: (err) => onError(extractErrorMessage(err)) },
    );
  }

  if (editing) {
    return (
      <tr>
        <td>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={`Edit name: ${group.name}`}
          />
        </td>
        <td>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onBlur={(e) => setCode(e.target.value.toUpperCase())}
            aria-label={`Edit code: ${group.name}`}
          />
        </td>
        <td>
          <input
            type="number"
            className={styles.sortInput}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            aria-label={`Edit sort order: ${group.name}`}
          />
        </td>
        <td colSpan={3} className={styles.actionsCell}>
          <Button variant="primary" size="sm" onClick={save} loading={updateMut.isPending}>
            Save
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setName(group.name);
              setCode(group.code);
              setSortOrder(String(group.sort_order));
              setEditing(false);
            }}
          >
            Cancel
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{group.name}</td>
      <td className={styles.codeCell}>{group.code}</td>
      <td>{group.sort_order}</td>
      <td>
        <button
          type="button"
          className={styles.statusBtn}
          onClick={toggleActive}
          disabled={updateMut.isPending}
          aria-label={`Toggle active: ${group.name}`}
        >
          {group.is_active ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="muted">Inactive</Badge>
          )}
        </button>
      </td>
      <td>{group.product_count ?? 0}</td>
      <td className={styles.actionsCell}>
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button variant="ghost" size="sm" icon={Trash2} onClick={onDelete}>
          Delete
        </Button>
      </td>
    </tr>
  );
}
